import { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import Avatar from './Avatar';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../locales/translations';

const Navigation = ({ children, onUserGuideSection }) => {
  const { user, logout } = useContext(AuthContext);
  const { currentLanguage, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userGuideExpanded, setUserGuideExpanded] = useState(location.pathname === '/guide');

  const navItems = [
    { path: '/feed', label: t('nav.feed'), icon: '🔍', shortLabel: 'Feed' },
    { path: '/groups', label: t('nav.groups'), icon: '👥', shortLabel: 'Groups' },
    { path: '/network', label: t('nav.myNetwork'), icon: '🤝', shortLabel: 'Network' },
    { path: '/messages', label: 'Messages', icon: '💬', shortLabel: 'Messages' },
    { path: '/events', label: t('nav.events'), icon: '📅', shortLabel: 'Events' },
    { path: '/resources', label: t('nav.resources'), icon: '📚', shortLabel: 'Resources' },
    { path: '/tenders', label: t('nav.tenders'), icon: '📋', shortLabel: 'Tenders' },
    { path: '/my-tenders', label: 'My Tenders', icon: '📊', shortLabel: 'My Tenders' },
    { path: '/guide', label: t('nav.userGuide'), icon: '📖', shortLabel: 'Guide', isGuide: true }
  ];

  const userGuideSections = [
    { id: 'getting-started', label: 'Getting Started', icon: '🚀' },
    { id: 'feed', label: 'Social Feed', icon: '🔍' },
    { id: 'groups', label: 'Groups & Communities', icon: '👥' },
    { id: 'network', label: 'My Network', icon: '🤝' },
    { id: 'events', label: 'Events & Meetings', icon: '📅' },
    { id: 'resources', label: 'Learning Resources', icon: '📚' },
    { id: 'tenders', label: 'Tenders & Opportunities', icon: '📋' }
  ];

  // Add admin dashboard for admin/moderator users
  const adminNavItems = user && (user.role === 'admin' || user.role === 'moderator') 
    ? [{ path: '/admin', label: 'Admin Center', icon: '⚙️', shortLabel: 'Admin' }] 
    : [];

  const handleLanguageChange = (e) => {
    changeLanguage(e.target.value);
  };

  const isActive = (path) => location.pathname === path;

  const handleUserGuideClick = (e) => {
    if (location.pathname === '/guide') {
      e.preventDefault();
      setUserGuideExpanded(!userGuideExpanded);
    } else {
      setUserGuideExpanded(true);
    }
  };

  const handleGuideSection = (sectionId) => {
    if (onUserGuideSection) {
      onUserGuideSection(sectionId);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <div className={`${sidebarCollapsed ? 'w-20' : 'w-64'} min-h-screen bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        {/* Logo Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className={`font-bold text-teal-600 transition-all duration-300 ${
                sidebarCollapsed ? 'text-xl' : 'text-2xl'
              }`}
            >
              {sidebarCollapsed ? 'LH' : 'Labamu Hub'}
            </Link>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarCollapsed ? "M4 6h16M4 12h16M4 18h16" : "M6 18L18 6M6 6l12 12"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 px-4 py-6">
          <div className="space-y-2">
            {[...navItems, ...adminNavItems].map((item) => (
              <div key={item.path}>
                {/* Main Navigation Item */}
                <Link
                  to={item.path}
                  onClick={item.isGuide ? handleUserGuideClick : undefined}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 group ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg shadow-teal-500/25'
                      : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50 hover:shadow-sm'
                  }`}
                  title={sidebarCollapsed ? item.shortLabel : ''}
                >
                  <span className="text-xl flex-shrink-0">{item.icon}</span>
                  {!sidebarCollapsed && (
                    <span className="font-medium text-sm">{item.label}</span>
                  )}
                  {item.isGuide && !sidebarCollapsed && isActive(item.path) && (
                    <span className={`ml-auto transition-transform duration-200 ${userGuideExpanded ? 'rotate-90' : ''}`}>
                      ▶
                    </span>
                  )}
                  {isActive(item.path) && (
                    <div className="absolute left-0 w-1 h-8 bg-white rounded-r-full"></div>
                  )}
                </Link>

                {/* User Guide Sub-menu */}
                {item.isGuide && isActive(item.path) && userGuideExpanded && !sidebarCollapsed && (
                  <div className="mt-2 ml-4 space-y-1">
                    {userGuideSections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => handleGuideSection(section.id)}
                        className="w-full flex items-center justify-start space-x-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-gray-600 hover:text-teal-600 hover:bg-teal-50 hover:shadow-sm text-left"
                      >
                        <span className="text-base flex-shrink-0">{section.icon}</span>
                        <span className="text-left">{section.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-gray-50 overflow-hidden flex flex-col">
        {/* Top Navigation Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-end">
            {/* User Profile Section - Now in Top Right */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center space-x-3 p-2 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Avatar user={user} size="medium" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role} Access</p>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Profile Dropdown */}
              {showProfileMenu && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  {/* Language Selector */}
                  <div className="px-4 py-2 border-b border-gray-100">
                    <select
                      value={currentLanguage}
                      onChange={handleLanguageChange}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="en">🇺🇸 English</option>
                      <option value="id">🇮🇩 Bahasa Indonesia</option>
                    </select>
                  </div>
                  
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    👤 Profile Settings
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    ⚙️ Account Settings
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setShowProfileMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    🚪 Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Navigation;